import _ from 'lodash';
import Element from 'ui/Element';
import mixin from 'privates/utils/mixin';
import {emptyStringIfNil, isNilOrEmptyString} from 'privates/utils';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import ClickableMixin from './mixins/ClickableMixin';
import * as members from 'privates/privateMembers';
import LinkableMixin from './mixins/LinkableMixin';
import imageUtils from 'privates/utils/imageUtils';
import {validate} from 'privates/validations/validation';
import {IMAGE_CLICK_ACTIONS} from 'privates/constants';

/**
 * @class Image
 * @summary Images are images stored in the <a href="https://support.wix.com/en/article/about-the-media-manager" target="_blank">Media Manager</a>
 *  or retrieved from an external web location.
 *
 *  The URL formats for images are:
 *
 *  + Images from the Media Manager: `wix:image://v1/<uri>/<filename>#originWidth=<width>&originHeight=<height>[&watermark=<watermark_manifest_string>]`
 *  + Images from the web: `http(s)://<image url>`
 * @memberof $w
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.LinkableMixin
 * @mixes $w.ClickableMixin
 * @viewername wysiwyg.viewer.components.WPhoto
 * @definitionId wysiwyg.viewer.components.WPhoto
 * @tagname image
 */
class Image extends Element {

    /**
     * @member alt
     * @label property
     * @summary Sets or gets an image's alt text.
     * @description
     *  An image's alternative text is typically a relatively short phrase that
     *  describes what the image depicts.
     *
     *  The alternative text is used:
     *
     *  + If the browser cannot display the image.
     *  + If the user is utilizing a screen reader.
     *  + By search engines to understand what images are on your site.
     * @snippet [Image-alt_get.es6=Get the image's alt text]
     * @snippet [Image-alt_set.es6=Set the image's alt text]
     * @type {String}
     * @default An empty string
     * @memberof $w.Image
     * @instance
     * @attr {String}
     */
    get alt() {
        const RMI = members._RMI.get(this);
        return RMI.getData(members._compId.get(this)).alt;
    }

  /**
   * @member alt
   * @memberof $w.Image
   * @param {String} value
   */
    set alt(value) {
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'alt',
            dataToValidate: {alt: value},
            compName: this.id
        });
        if (!isValid) {
            return;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        RMI.setData(compId, {
            alt: emptyStringIfNil(value)
        });
    }

    /**
     * @member tooltip
     * @label property
     * @summary Sets or gets an image's tooltip.
     * @description
     *  An image's tooltip text is displayed when a user hovers over the image
     *  or when the image opens in a popup.
     * @snippet [Image-tooltip_get.es6=Get the image's tooltip text]
     * @snippet [Image-tooltip_set.es6=Set the image's tooltip text]
     * @type {String}
     * @default An empty string
     * @memberof $w.Image
     * @instance
     * @attr {String}
     */
    get tooltip() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getData(compId).title;
    }

  /**
   * @member tooltip
   * @memberof $w.Image
   * @param {String} value
   */
    set tooltip(value) {
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'tooltip',
            dataToValidate: {tooltip: value},
            compName: this.id
        });
        if (!isValid) {
            return;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const title = emptyStringIfNil(value);
        RMI.setData(compId, {title});
    }

    /**
     * @member src
     * @label property
     * @summary Sets or gets the file location of the image.
     * @description
     *  Setting the `src` property changes the displayed image to
     *  the image found at the new `src` value.
     *
     *  Getting the `src` property returns the location of the
     *  current image file.
     *
     *  The image file can either be an image file from the <a href="https://support.wix.com/en/article/about-the-media-manager" target="_blank">Media Manager</a>
     *  or an external file from any web location.
     *
     *  The URL formats supported are:
     *
     *  + Images from the Media Manager: `wix:image://v1/<uri>/<filename>#originWidth=<width>&originHeight=<height>[&watermark=<watermark_manifest_string>]`
     *  + Images from the web: `http(s)://<image url>`
     * @snippet [Image-src_get.es6=Get the image's location]
     * @snippet [Image-src_set_wix.es6=Set the image to be an image from the Media Manager]
     * @snippet [Image-src_set_web.es6=Set the image to be an image from the web]
     * @type {String}
     * @default An empty string
     * @memberof $w.Image
     * @instance
     * @attr {String}
     */
    get src() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getData(compId) || {};

        return imageUtils.buildFullUrlFromUri(compData.uri, compData.width, compData.height, compData.title);
    }

  /**
   * @member src
   * @memberof $w.Image
   * @param {String} value
   */
    set src(value) {
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'src',
            dataToValidate: {src: value},
            compName: this.id
        });
        if (!isValid) {
            return;
        }

        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        let imageData;

        if (isNilOrEmptyString(value)) {
            imageData = {uri: ''};
        } else{
            imageData = imageUtils.convertImageToModelFormatAndReportIfError(value, 'src', 'src');
            if (!imageData){
                return;
            }
        }

        imageData.crop = null;

        RMI.setData(compId, imageData);
    }

    /**
     * @member clickAction
     * @label property
     * @summary Sets or gets the action that occurs when an image is clicked.
     * @description
     *  Setting the `clickAction` property sets what happens when
     *  an image is clicked.
     *
     *  The value can be set to:
     *
     *  + `"none"`: Nothing happens.
     *  + `"expand"`: The image opens in a popup window
     *  + `"link"`: The image's [`link`](#link) opens.
     *  + `"magnified"`: The cursor becomes a magnifying glass when a visitor hovers over the image.
     *
     *
     *  Getting the `clickAction` property returns what happens when
     *  an image is clicked.
     * @snippet [Image-clickAction_get.es6=Get the action that occurs when an image is clicked]
     * @snippet [Image-clickAction_set.es6=Set the action that occurs when an image is clicked]
     * @type {String}
     * @memberof $w.Image
     * @instance
     */
    get clickAction() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const clickAction = RMI.getProps(compId).onClickBehavior;
        return _.findKey(IMAGE_CLICK_ACTIONS, (v) => v === clickAction);
    }

  /**
   * @member clickAction
   * @memberof $w.Image
   * @param {String} action
   */
    set clickAction(action) {
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'clickAction',
            dataToValidate: {clickAction: action},
            compName: this.id
        });

        if (!isValid) {
            return;
        }

        const clickAction = _.isNil(action) ? IMAGE_CLICK_ACTIONS.none : IMAGE_CLICK_ACTIONS[action];

        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        RMI.setProps(compId, {onClickBehavior: clickAction});
    }

    toJSON() {
        const {id, type, global, rendered, alt, tooltip, src} = this;
        return {id, type, global, rendered, alt, tooltip, src};
    }
}

mixin(Image.prototype, HiddenCollapsedMixin.prototype);
mixin(Image.prototype, LinkableMixin.prototype);
mixin(Image.prototype, ClickableMixin.prototype);

export default Image;



// WEBPACK FOOTER //
// ./js/modules/ui/Image.es6